// Input bindings for the 2D platformer scene: which keyboard codes and
// gamepad buttons drive each action. Pure data plus a merge helper, no DOM,
// so a game can author and unit-test its own layout. `Platformer2DScene`
// reads these every frame through `../../input/inputState.ts`.

/** Every action the platformer reads. `left`/`right` are level-triggered
 * (held), `jump`/`rewind`/`attack` are read both as an edge and as a level,
 * the rest are edge-triggered. */
export type PlatformerAction =
  | 'left'
  | 'right'
  | 'jump'
  | 'dash'
  | 'teleport'
  | 'shadowForm'
  | 'rewind'
  | 'ghostDouble'
  | 'attack';

/** Ordered list of every {@link PlatformerAction}, for iteration and tests. */
export const PLATFORMER_ACTIONS: readonly PlatformerAction[] = [
  'left',
  'right',
  'jump',
  'dash',
  'teleport',
  'shadowForm',
  'rewind',
  'ghostDouble',
  'attack',
];

export interface PlatformerBindings {
  /** `KeyboardEvent.code` values per action (e.g. `'KeyW'`, `'Space'`). */
  keyboard: Record<PlatformerAction, string[]>;
  /** Standard Gamepad API button indices per action. The left stick's
   * horizontal axis always drives `left`/`right` as well. */
  gamepad: Record<PlatformerAction, number[]>;
}

/**
 * The layout every scene used before bindings were configurable, plus the
 * attack action. Gamepad: A jumps (0, and d-pad up 12), X attacks (2),
 * RB dashes (5), Y or B teleports (3, 1), LB shadow-forms (4), LT rewinds
 * (6), RT ghost-doubles (7), d-pad left/right (14, 15) moves.
 */
export const DEFAULT_PLATFORMER_BINDINGS: PlatformerBindings = {
  keyboard: {
    left: ['KeyA', 'ArrowLeft'],
    right: ['KeyD', 'ArrowRight'],
    jump: ['Space', 'KeyW', 'ArrowUp'],
    dash: ['ShiftLeft', 'ShiftRight', 'KeyJ', 'KeyX'],
    teleport: ['KeyC', 'KeyK'],
    shadowForm: ['KeyQ', 'KeyI'],
    rewind: ['KeyR', 'KeyE'],
    ghostDouble: ['KeyG', 'KeyF'],
    attack: ['KeyL', 'KeyZ', 'Slash'],
  },
  gamepad: {
    left: [14],
    right: [15],
    jump: [0, 12],
    dash: [5],
    teleport: [3, 1],
    shadowForm: [4],
    rewind: [6],
    ghostDouble: [7],
    attack: [2],
  },
};

/** A partial layout: only the actions a game wants to change. */
export interface PlatformerBindingsOverrides {
  keyboard?: Partial<Record<PlatformerAction, string[]>>;
  gamepad?: Partial<Record<PlatformerAction, number[]>>;
}

/**
 * Merge `overrides` on top of `base` (the defaults unless given) action by
 * action: an action listed in `overrides` replaces that action's list
 * outright (so a game can unbind by passing `[]`), every other action keeps
 * the base list. Returns a fresh object; neither input is mutated.
 */
export function mergePlatformerBindings(
  overrides: PlatformerBindingsOverrides | undefined,
  base: PlatformerBindings = DEFAULT_PLATFORMER_BINDINGS,
): PlatformerBindings {
  const keyboard = {} as Record<PlatformerAction, string[]>;
  const gamepad = {} as Record<PlatformerAction, number[]>;
  for (const action of PLATFORMER_ACTIONS) {
    const k = overrides?.keyboard?.[action];
    keyboard[action] = (k ?? base.keyboard[action] ?? []).slice();
    const g = overrides?.gamepad?.[action];
    gamepad[action] = (g ?? base.gamepad[action] ?? []).slice();
  }
  return { keyboard, gamepad };
}

/**
 * Every code or button bound to more than one action, as
 * `{ binding, actions }` pairs. Empty when the layout is unambiguous. A
 * scene tolerates duplicates (both actions fire), so this is a lint for
 * authoring tools and tests rather than a runtime guard.
 */
export function findDuplicatePlatformerBindings(bindings: PlatformerBindings): {
  keyboard: { code: string; actions: PlatformerAction[] }[];
  gamepad: { button: number; actions: PlatformerAction[] }[];
} {
  const byCode = new Map<string, PlatformerAction[]>();
  const byButton = new Map<number, PlatformerAction[]>();
  for (const action of PLATFORMER_ACTIONS) {
    for (const code of bindings.keyboard[action] ?? []) {
      const list = byCode.get(code) ?? [];
      if (!list.includes(action)) list.push(action);
      byCode.set(code, list);
    }
    for (const button of bindings.gamepad[action] ?? []) {
      const list = byButton.get(button) ?? [];
      if (!list.includes(action)) list.push(action);
      byButton.set(button, list);
    }
  }
  return {
    keyboard: [...byCode.entries()].filter(([, a]) => a.length > 1).map(([code, actions]) => ({ code, actions })),
    gamepad: [...byButton.entries()].filter(([, a]) => a.length > 1).map(([button, actions]) => ({ button, actions })),
  };
}
